// Reconciliation — the money page. Ghosts, orphans, unowned, conflicts.
// Migrated to Cyan Room design system.

import { pbAuthedFetch } from '../authHeaders';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTableControls, type Column } from '../hooks/useTableControls';

// Maps the `?filter=` query param set by the summary cards to the finding_type
// values that back it. "conflicts" has no dedicated finding_type — conflicts
// live in the assertion table — so it's matched by CI membership instead.
const CATEGORY_FINDING_TYPES: Record<string, string[]> = {
  ghosts: ['ghost_ci'],
  orphans: ['orphan_ci'],
  unowned: ['unowned_ci'],
};

const CATEGORY_LABELS: Record<string, string> = {
  ghosts: 'Ghosts',
  orphans: 'Orphans',
  unowned: 'Unowned',
  conflicts: 'Conflicts',
};

export function ReconciliationPage() {
  const filterParam = new URLSearchParams(window.location.search).get('filter');
  const { data: summary, isLoading: summaryLoading, error: summaryError, refetch: refetchSummary } = useQuery({
    queryKey: ['reconciliation-summary'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/reconciliation/summary');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: conflictsData, isLoading: conflictsLoading } = useQuery({
    queryKey: ['conflicts'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/reconciliation/conflicts');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: findingsData, isLoading: findingsLoading } = useQuery({
    queryKey: ['findings'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/reconciliation/findings?limit=50');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const conflicts = conflictsData?.conflicts || [];
  const allFindings = findingsData?.findings || [];

  const findings = useMemo(() => {
    if (!filterParam) return allFindings;
    if (filterParam === 'conflicts') {
      const conflictCiIds = new Set(conflicts.flatMap((c: any) => c.ci_ids || []));
      return allFindings.filter((f: any) => conflictCiIds.has(f.ci_id));
    }
    const types = CATEGORY_FINDING_TYPES[filterParam];
    if (!types) return allFindings;
    return allFindings.filter((f: any) => types.includes(f.finding_type));
  }, [allFindings, conflicts, filterParam]);

  const conflictColumns: Column<any>[] = [
    {
      key: 'attribute',
      label: 'Attribute',
      sortable: true,
      accessor: (row) => row.attribute,
    },
    {
      key: 'ci_count',
      label: 'CIs Affected',
      sortable: true,
      accessor: (row) => row.ci_count,
    },
  ];

  const conflictsTable = useTableControls(conflicts, conflictColumns, {
    storageKey: 'reconciliation-conflicts',
    keyAccessor: (row) => row.attribute,
  });

  const findingColumns: Column<any>[] = [
    {
      key: 'severity',
      label: 'Severity',
      sortable: true,
      accessor: (row) => row.severity,
      filterOptions: [
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
        { value: 'info', label: 'Info' },
      ],
    },
    {
      key: 'finding_type',
      label: 'Type',
      sortable: true,
      accessor: (row) => row.finding_type,
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      accessor: (row) => row.title,
    },
    {
      key: 'canonical_key',
      label: 'CI',
      sortable: true,
      accessor: (row) => row.canonical_key,
    },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      accessor: (row) => row.state,
      filterOptions: [
        { value: 'open', label: 'Open' },
        { value: 'accepted', label: 'Accepted' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'dismissed', label: 'Dismissed' },
      ],
    },
    {
      key: 'detected_at',
      label: 'Detected',
      sortable: true,
      accessor: (row) => row.detected_at,
      dateField: true,
    },
  ];

  const findingsTable = useTableControls(findings, findingColumns, {
    storageKey: 'reconciliation-findings',
    keyAccessor: (row) => row.id,
  });

  const isLoading = summaryLoading || conflictsLoading || findingsLoading;

  /* ── helpers ─────────────────────────────────────────────────────────── */

  function severityPill(severity: string) {
    const cls =
      severity === 'critical' || severity === 'high'
        ? 'pill pill--risk'
        : severity === 'medium'
          ? 'pill pill--warn'
          : 'pill pill--ok';
    return (
      <span className={cls}>
        <i></i> {severity}
      </span>
    );
  }

  function statePill(state: string) {
    const cls =
      state === 'open'
        ? 'pill pill--risk'
        : state === 'accepted'
          ? 'pill pill--warn'
          : 'pill pill--ok';
    return (
      <span className={cls}>
        <i></i> {state}
      </span>
    );
  }

  function sortDir(key: string, sortKey: string, sortDir: string) {
    if (key !== sortKey) return undefined as 'ascending' | 'descending' | undefined;
    return sortDir as 'ascending' | 'descending';
  }

  /* ── loading ─────────────────────────────────────────────────────────── */

  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Reconciliation</p>
              <h1 className="pagehead__title">Drift &amp; Conflict</h1>
              <div className="pagehead__facts">
                <span className="fact">Reconciliation exceptions — the four categories that drive accountability</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading reconciliation data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ── error ───────────────────────────────────────────────────────────── */

  if (summaryError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Reconciliation</p>
              <h1 className="pagehead__title">Drift &amp; Conflict</h1>
              <div className="pagehead__facts">
                <span className="fact">Reconciliation exceptions — the four categories that drive accountability</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load reconciliation data</h3>
                <p className="state__body">
                  Could not connect to the reconciliation API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetchSummary()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ── main ────────────────────────────────────────────────────────────── */

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Reconciliation</p>
            <h1 className="pagehead__title">Drift &amp; Conflict</h1>
            <div className="pagehead__facts">
              <span className="fact">Reconciliation exceptions — the four categories that drive accountability</span>
              <span className="fact">{findings.length} open findings</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Completeness notice */}
        <div className="notice notice--info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <p className="notice__title">Reconciliation Overview</p>
            <p className="notice__body">
              ATLAS tracks four categories of reconciliation exceptions: <strong>ghosts</strong> (in CMDB but never seen on network), <strong>orphans</strong> (on network but in no system of record), <strong>unowned</strong> assets (no ownership edge), and <strong>conflicts</strong> (sources disagree). Each drives a different accountability action.
            </p>
          </div>
        </div>

        {/* Four headline counters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Reconciliation summary">
          <a href="/reconciliation?filter=ghosts" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="ind ind--watch" style={{ cursor: 'pointer' }}>
              <div className="ind__head">
                <p className="ind__abbr">Ghosts</p>
                <p className="ind__full">In CMDB/procurement, never observed on network</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary?.ghosts ?? '—'}</p>
              </div>
              <p className="ind__reading">
                <i style={{ background: 'var(--gold)' }}></i>
                <b style={{ color: 'var(--gold)' }}>Monitor</b> — present in CMDB but not detected on network
              </p>
            </div>
          </a>

          <a href="/reconciliation?filter=orphans" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="ind ind--watch" style={{ cursor: 'pointer' }}>
              <div className="ind__head">
                <p className="ind__abbr">Orphans</p>
                <p className="ind__full">Observed on network, in no system of record</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary?.orphans ?? '—'}</p>
              </div>
              <p className="ind__reading">
                <i style={{ background: 'var(--gold)' }}></i>
                <b style={{ color: 'var(--gold)' }}>Monitor</b> — detected but not tracked in any system
              </p>
            </div>
          </a>

          <a href="/reconciliation?filter=unowned" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="ind ind--breach" style={{ cursor: 'pointer' }}>
              <div className="ind__head">
                <p className="ind__abbr">Unowned</p>
                <p className="ind__full">No ownership edge or owning account disabled</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary?.unowned ?? '—'}</p>
              </div>
              <p className="ind__reading">
                <i></i>
                <b>Action required</b> — no accountable owner identified
              </p>
            </div>
          </a>

          <a href="/reconciliation?filter=conflicts" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="ind ind--breach" style={{ cursor: 'pointer' }}>
              <div className="ind__head">
                <p className="ind__abbr">Conflicts</p>
                <p className="ind__full">Sources disagree on attribute values</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary?.conflicts ?? '—'}</p>
              </div>
              <p className="ind__reading">
                <i></i>
                <b>Action required</b> — data inconsistency detected
              </p>
            </div>
          </a>
        </div>

        {/* Conflicts by attribute */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Conflicts</p>
              <h2 className="box__title">Conflicts by Attribute</h2>
            </div>
          </div>
          <div className="box__toolbar">
            <div className="search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="search"
                placeholder="Search attributes…"
                value={conflictsTable.search}
                onChange={(e) => conflictsTable.setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="box__body box__body--flush">
            {conflictsTable.paged.length > 0 ? (
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>
                        <button
                          className="th"
                          aria-sort={sortDir('attribute', conflictsTable.sortKey, conflictsTable.sortDir)}
                          onClick={() => conflictsTable.setSort('attribute')}
                        >
                          Attribute
                        </button>
                      </th>
                      <th>
                        <button
                          className="th th--num"
                          aria-sort={sortDir('ci_count', conflictsTable.sortKey, conflictsTable.sortDir)}
                          onClick={() => conflictsTable.setSort('ci_count')}
                        >
                          CIs Affected
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {conflictsTable.paged.map((row: any) => (
                      <tr key={row.attribute}>
                        <td className="td--id">{row.attribute}</td>
                        <td className="td--num">
                          <strong>{row.ci_count}</strong>
                          <span style={{ color: 'var(--ink-45)', fontSize: 'var(--text-sm)', marginLeft: 'var(--sp-2)' }}>
                            CIs with conflicting values
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3 className="state__title">No conflicts detected</h3>
                <p className="state__body">
                  All sources agree on attribute values. The registry is consistent.
                </p>
              </div>
            )}
          </div>
          {conflictsTable.totalPages > 1 && (
            <div className="box__foot">
              <span>
                Showing <b>{conflictsTable.paged.length}</b> of <b>{conflictsTable.totalFiltered}</b>
              </span>
              <div className="pager">
                <button className="pg" disabled={conflictsTable.page <= 1} onClick={() => conflictsTable.setPage(conflictsTable.page - 1)}>‹</button>
                {Array.from({ length: Math.min(conflictsTable.totalPages, 7) }, (_, i) => {
                  const pg = i + 1;
                  return (
                    <button
                      key={pg}
                      className="pg"
                      aria-current={pg === conflictsTable.page ? 'page' : undefined}
                      onClick={() => conflictsTable.setPage(pg)}
                    >
                      {pg}
                    </button>
                  );
                })}
                <button className="pg" disabled={conflictsTable.page >= conflictsTable.totalPages} onClick={() => conflictsTable.setPage(conflictsTable.page + 1)}>›</button>
              </div>
            </div>
          )}
        </div>

        {/* Open findings */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Findings</p>
              <h2 className="box__title">Open Findings</h2>
            </div>
            <div className="box__head-actions">
              {filterParam && (
                <span className="fact">
                  Filtered: {CATEGORY_LABELS[filterParam] || filterParam}{' '}
                  <a href="/reconciliation" style={{ marginLeft: 'var(--sp-2)' }}>Clear</a>
                </span>
              )}
              <span className="fact">{findings.length} findings</span>
            </div>
          </div>
          <div className="box__toolbar">
            <div className="search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="search"
                placeholder="Search findings…"
                value={findingsTable.search}
                onChange={(e) => findingsTable.setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="box__body box__body--flush">
            {findingsTable.paged.length > 0 ? (
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>
                        <button
                          className="th"
                          aria-sort={sortDir('severity', findingsTable.sortKey, findingsTable.sortDir)}
                          onClick={() => findingsTable.setSort('severity')}
                        >
                          Severity
                        </button>
                      </th>
                      <th>
                        <button
                          className="th"
                          aria-sort={sortDir('finding_type', findingsTable.sortKey, findingsTable.sortDir)}
                          onClick={() => findingsTable.setSort('finding_type')}
                        >
                          Type
                        </button>
                      </th>
                      <th>
                        <button
                          className="th"
                          aria-sort={sortDir('title', findingsTable.sortKey, findingsTable.sortDir)}
                          onClick={() => findingsTable.setSort('title')}
                        >
                          Title
                        </button>
                      </th>
                      <th>
                        <button
                          className="th"
                          aria-sort={sortDir('canonical_key', findingsTable.sortKey, findingsTable.sortDir)}
                          onClick={() => findingsTable.setSort('canonical_key')}
                        >
                          CI
                        </button>
                      </th>
                      <th>
                        <button
                          className="th"
                          aria-sort={sortDir('state', findingsTable.sortKey, findingsTable.sortDir)}
                          onClick={() => findingsTable.setSort('state')}
                        >
                          State
                        </button>
                      </th>
                      <th>
                        <button
                          className="th"
                          aria-sort={sortDir('detected_at', findingsTable.sortKey, findingsTable.sortDir)}
                          onClick={() => findingsTable.setSort('detected_at')}
                        >
                          Detected
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {findingsTable.paged.map((row: any) => (
                      <tr key={row.id}>
                        <td>{severityPill(row.severity)}</td>
                        <td className="td--id">{row.finding_type}</td>
                        <td className="td--name">{row.title}</td>
                        <td className="td--id">{row.canonical_key || '—'}</td>
                        <td>{statePill(row.state)}</td>
                        <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                          {new Date(row.detected_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3 className="state__title">No findings</h3>
                <p className="state__body">
                  The registry is clean — or no data has been ingested yet.
                </p>
              </div>
            )}
          </div>
          {findingsTable.totalPages > 1 && (
            <div className="box__foot">
              <span>
                Showing <b>{findingsTable.paged.length}</b> of <b>{findingsTable.totalFiltered}</b>
              </span>
              <div className="pager">
                <button className="pg" disabled={findingsTable.page <= 1} onClick={() => findingsTable.setPage(findingsTable.page - 1)}>‹</button>
                {Array.from({ length: Math.min(findingsTable.totalPages, 7) }, (_, i) => {
                  const pg = i + 1;
                  return (
                    <button
                      key={pg}
                      className="pg"
                      aria-current={pg === findingsTable.page ? 'page' : undefined}
                      onClick={() => findingsTable.setPage(pg)}
                    >
                      {pg}
                    </button>
                  );
                })}
                <button className="pg" disabled={findingsTable.page >= findingsTable.totalPages} onClick={() => findingsTable.setPage(findingsTable.page + 1)}>›</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
